import { GraphCard } from '@fareye/common'
import { Flex, Graph, Grid, RenderControl, Span, InfoIcon } from '@fareye/ui'
import {
  AxisFormatter,
  cancelToken,
  get,
  getQueryParams,
  isEmpty,
  removeAlertSubText,
  responseParser
} from '@fareye/utils'
import queryString from 'query-string'
import React from 'react'
import colors from '../../../config/colors'
import { inTransitConstant } from '../../../config/constants'
import { TransporterStatus } from '../../../style/styled'
import { getYKeys } from '../../../utility/funcs'
import { IRouteDetails } from '../../../utility/types'
import { PieGraph } from '../components/Pie'
import TransporterPerformanceCard from '../components/TransporterPerformanceCard'

const RouteDetails = ({
  match,
  config,
  endDate,
  location,
  startDate,
  searchQuery
}: IRouteDetails) => {
  const searchParams = queryString.parse(location.search)
  const { id }: any = match.params
  const idParam = id ? decodeURIComponent(id).split('-') : []
  const source = cancelToken().source()
  const {
    config: {
      apiConfig,
      metricsCount,
      apiTransporterPerformance,
      graphsToShow = {}
    },
    styling: { style, className, variant }
  } = config
  const {
    tripStatusGraph,
    exceptionsGraph,
    transporterPerformanceGraph
  } = graphsToShow
  const [apiResponse, setApiResponse] = React.useState<any>({
    data: [],
    isLoading: false,
    isError: false,
    isSuccess: false,
    message: ''
  })

  const query = getQueryParams({
    ...searchParams,
    startDate,
    endDate,
    ...searchQuery,
    origin: searchParams?.origin || idParam[0],
    destination: searchParams?.destination || idParam[1],
    ...(apiConfig && apiConfig.queryParams)
  })

  async function fetchData() {
    try {
      setApiResponse({ ...apiResponse, isLoading: true })
      const {
        data: { content },
        message,
        error
      } = responseParser(
        await get(`${apiConfig && apiConfig.apiURL}?${query}`, source.token)
      )
      if (error) {
        setApiResponse({
          ...apiResponse,
          isError: true,
          data: content,
          isSuccess: true,
          isLoading: false,
          message
        })
      } else {
        setApiResponse({
          ...apiResponse,
          isError: false,
          data: content,
          isSuccess: true,
          isLoading: false,
          message:
            apiConfig &&
            apiConfig.apiDataState &&
            apiConfig.apiDataState.noDataMessage
        })
      }
    } catch (err:any) {
      const { message, error } = responseParser(err.response)
      setApiResponse({
        ...apiResponse,
        isSuccess: false,
        isError: true,
        isLoading: false,
        message
      })
      console.error('App Error:', error)
    }
  }

  React.useEffect(() => {
    fetchData()
    return () => {
      source.cancel('Unsubscribe Before Unmount')
    }
    // eslint-disable-line
  }, [query])

  const { data } = apiResponse
  const exceptions: Array<any> = []
  let vehicleMetricsTotal: any = null
  const hasContent = !isEmpty(data)
  const transporterData = hasContent ? data[0]['data'][0] : []
  const exceptionSingle = transporterData['exceptions']
    ? transporterData['exceptions']
    : []
  vehicleMetricsTotal = transporterData ? transporterData['vehicleMetrics'] : {}
  let sumOfTrips: number = 0
  const totalExceptions = exceptionSingle.reduce(
    (sum: number, item: any) => sum + item.count,
    0
  )

  const exceptionsConfig: any = {}
  let colorsCount = 0

  data.map((item: any, index: any) => {
    let tempExceptions: any = {}
    const { vehicleMetrics, totalTrips } = item.data[0]
    sumOfTrips = sumOfTrips + totalTrips
    if (index !== 0) {
      Object.keys(vehicleMetrics).forEach((key: string, index: number) => {
        vehicleMetricsTotal[key] =
          vehicleMetricsTotal[key] + vehicleMetrics[key]
      })
    }

    item.data[0].exceptions.map((item: any) => {
      tempExceptions[removeAlertSubText(item.type)] = item.count
      exceptionsConfig[removeAlertSubText(item.type)] = {
        color: colors.colors[colorsCount++],
        stackId: 'a'
      }
    })
    if (item.data[0].exceptions.length) {
      exceptions.push({
        label: AxisFormatter(item.date),
        ...tempExceptions
      })
    }
  })

  exceptions.sort((a: any, b: any) => {
    const ddMM1 = a.label.split('/')
    const ddMM2 = b.label.split('/')
    if (ddMM1[1] > ddMM2[1]) {
      return 1
    } else if (ddMM1[1] === ddMM2[1]) {
      return a.label > b.label ? 1 : -1
    } else {
      return -1
    }
  })

  const tripStatusHeading = (
    <>
      {inTransitConstant.tripStatusLabel}
      <InfoIcon style={{ marginLeft: 5 }} title={`Trip Completion Status`} />
    </>
  )

  const exceptionHeading = (
    <>
      {inTransitConstant.alertsLabel}
      <InfoIcon style={{ marginLeft: 5 }} title={`Alerts Wise Distribution`} />
    </>
  )

  return (
    <RenderControl state={apiResponse} variant={'m'}>
      <Flex
        p={3}
        style={{
          width: 'calc(100% - 32px)',
          overflow: 'auto hidden',
          ...style
        }}
        className={className}
      >
        <Flex style={{ padding: '2px 4px', overflow: 'auto hidden' }}>
          {tripStatusGraph && tripStatusGraph.show && (
            <GraphCard
              heading={tripStatusHeading}
              data-config-id="tripStatusGraph"
            >
              <PieGraph
                data={vehicleMetricsTotal ? vehicleMetricsTotal : {}}
                metric={metricsCount}
                sumOfTrips={sumOfTrips}
              />
            </GraphCard>
          )}
          {exceptionsGraph && exceptionsGraph.show ? (
            <GraphCard graphId="exceptions" heading={exceptionHeading}>
              {startDate == endDate ? (
                exceptionSingle.length ? (
                  exceptionSingle.map((item: any) => (
                    <Grid
                      key={`${item.type}`}
                      gridTemplateColumns="2fr 3fr 1fr"
                      style={{ marginBottom: '10px' }}
                    >
                      <Span
                        style={{ width: 120, fontWeight: 200 }}
                        variant="ellipsis"
                      >
                        {removeAlertSubText(item.type)}
                      </Span>
                      <TransporterStatus
                        value={(item.count / totalExceptions) * 100}
                      />
                      <Span style={{ textAlign: 'center' }}>{item.count}</Span>
                    </Grid>
                  ))
                ) : (
                  <Span>{inTransitConstant.noAlertsLabel}</Span>
                )
              ) : !isEmpty(exceptionsConfig) ? (
                <Graph
                  data={exceptions}
                  type="bar"
                  config={{
                    colors: {},
                    legendsList: ['']
                  }}
                  ratio={1.3}
                  selector="exceptions"
                  XLabel={'Date'}
                  YLabel={'Count of Alerts'}
                  XLabelKey={'label'}
                  YLabelKey={getYKeys(exceptions)}
                  stacked="tower"
                />
              ) : (
                <Span>{inTransitConstant.noAlertsLabel}</Span>
              )}
            </GraphCard>
          ) : null}
          {transporterPerformanceGraph && transporterPerformanceGraph.show ? (
            <TransporterPerformanceCard
              match={match}
              location={location}
              history={history}
              config={apiTransporterPerformance}
              startDate={startDate}
              endDate={endDate}
              searchQuery={searchQuery}
            />
          ) : null}
        </Flex>
      </Flex>
    </RenderControl>
  )
}
export default RouteDetails
